import {
  Component,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';

import trim from 'lodash-es/trim';
import { Store } from '@ngxs/store';
import { filter, switchMap } from 'rxjs';
import { MentionConfig } from 'angular-mentions';
import { UserState } from '@state/user/state';
import { RelatedToType } from '@shared/enums';
import { User } from '@core/models/user.types';
import { AccountState } from '@state/account/state';
import { Comment } from '@core/models/comment.types';
import { Account, Team } from '@core/models/account.types';
import { Project } from '@modules/projects/projects.types';
import { ConfirmService } from '../dialog/confirm/confirm.service';
import { QUILL_MODULES, QUILL_MODULES_TEXTAREA } from '@shared/Utils/quill-editor.utils';
import { CommentsService } from '@core/services/comments.service';
import { MentionsService } from '@shared/services/mentions.service';
import { ProjectsService } from '@modules/projects/projects.service';

@Component({
  selector: 'comment-view',
  templateUrl: './comment-view.component.html',
  styleUrls: ['./comment-view.component.scss'],
})
export class CommentViewComponent {
  @Input() account: Account;
  @Input() relatedToId: number;
  @Input() relatedToType: RelatedToType;
  @Input() readonly: boolean = false;
  @Input() textAreaAtBottom = false;
  @Input() compact = false;
  @Input() reverseOrder = false;
  @Input() inlineConfirm = false;
  @Output() commentChanges = new EventEmitter<boolean>();
  @Output() loaded = new EventEmitter<void>();

  get displayedComments(): Comment[] {
    if (!this.comments) return [];
    return this.reverseOrder ? [...this.comments].reverse() : this.comments;
  }

  private readonly destroyRef = inject(DestroyRef);

  public comments: Comment[];
  public isPosting = false;
  public isLoading = true;
  public commentForm: UntypedFormGroup;
  public replyCommentForm: UntypedFormGroup;
  public editCommentForm: UntypedFormGroup;
  public user: User = this.store.selectSnapshot(UserState.getUser);
  public users: User[] = this.store.selectSnapshot(UserState.getUsers);
  public teams: Team[];
  public quillModules = QUILL_MODULES;
  public mentionConfig: MentionConfig;
  public confirmingDeleteId: number | null = null;

  constructor(
    private readonly _commentsService: CommentsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _projectsService: ProjectsService,
    private readonly _confirmService: ConfirmService,
    private readonly store: Store,
    readonly mentionsService: MentionsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const changedRelatedToId = changes.relatedToId && !changes.relatedToId.isFirstChange();
    const changedRelatedToType = changes.relatedToType && !changes.relatedToType.isFirstChange();

    if (changedRelatedToId || changedRelatedToType) {
      this.loadComments();
    }
  }

  ngOnInit(): void {
    if (!this.account) {
      this.account = this.store.selectSnapshot(AccountState.getAccount);
    }

    this._commentsService.accountId = this.account.id;
    this.buildForms();
    this.quillModules = this.compact ? QUILL_MODULES_TEXTAREA : QUILL_MODULES;
    this.mentionConfig = this.mentionsService.getDefaultMentionConfig();
    this.populateMentionContactAssociations();
    this.loadComments();
  }

  private buildForms(): void {
    this.commentForm = this._formBuilder.group({
      body: ['', [Validators.required]],
    });

    this.editCommentForm = this._formBuilder.group({
      id: [],
      body: ['', [Validators.required]],
    });

    this.replyCommentForm = this._formBuilder.group({
      id: [],
      parent_id: [],
      body: ['', [Validators.required]],
    });
  }

  private populateMentionContactAssociations(): void {
    this._projectsService.project$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project: Project) => {
      if (project) {
        this.mentionConfig = this.mentionsService.getDefaultMentionConfig([
          {
            items: project?.contact_associations?.map((association) => {
              if (association?.contact) {
                const { first_name, last_name } = association.contact;
                return {
                  ...association.contact,
                  full_name: trim(`${first_name ? first_name : ''} ${last_name ? last_name : ''}`) || 'unknown',
                };
              }
              return { first_name: '', last_name: '' };
            }),
            triggerChar: '#',
            labelKey: 'full_name',
            allowSpace: true,
            maxItems: 4,
            mentionSelect: this.mentionsService.defaultHashtagMentionSelect.bind(this.mentionsService),
          },
        ]);
      }
    });
  }

  private loadComments(): void {
    this.isLoading = true;
    this._commentsService
      .getComments(this.relatedToId, this.relatedToType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((comments) => {
        this.comments = comments;
        this.isLoading = false;
        this._changeDetectorRef.markForCheck();
        this.loaded.emit();
      });
  }

  private cancelAllCommentStates(mode: 'editing' | 'replying'): void {
    if (!this.comments) return;

    const form = mode === 'editing' ? this.editCommentForm : this.replyCommentForm;

    const allComments = this.comments.reduce((acc, comment) => {
      acc.push(comment);
      if (comment.children) {
        acc.push(...comment.children);
      }
      return acc;
    }, []);

    allComments.forEach((comment) => {
      comment[mode] = false;
    });

    form.reset();
  }

  saveComment(formDirective: FormGroupDirective): void {
    const comment = { ...this.commentForm.value };

    comment.body = this.mentionsService.transformMentionsForBackend(comment.body);
    comment.commentable_type = this.relatedToType;
    comment.commentable_id = this.relatedToId;

    this.isPosting = true;

    this._commentsService
      .createComment(comment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isPosting = false;
        formDirective.resetForm();
        this.commentForm.reset();
        this.loadComments();
        this.commentChanges.emit(true);
      });
  }

  editComment(comment: Comment): void {
    this.cancelAllCommentStates('editing');

    const sanitizedBody = this.mentionsService.sanitizeCommentBody(comment.body);
    this.editCommentForm.patchValue({
      id: comment.id,
      body: sanitizedBody,
    });
    comment.editing = true;
  }

  replyToComment(comment: Comment): void {
    this.cancelAllCommentStates('replying');
    comment.replying = true;
    this.replyCommentForm.get('parent_id').setValue(comment.id);
  }

  cancelCommentReply(comment: Comment): void {
    comment.replying = false;
    this.replyCommentForm.get('parent_id').setValue(null);
  }

  saveCommentReply(): void {
    const comment = { ...this.replyCommentForm.value };

    comment.body = this.mentionsService.transformMentionsForBackend(comment.body);
    comment.commentable_type = this.relatedToType;
    comment.commentable_id = this.relatedToId;

    this._commentsService
      .createComment(comment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.replyCommentForm.reset();
        this.loadComments();
        this.commentChanges.emit(true);
      });
  }

  isConfirmingDelete(comment: Comment): boolean {
    return this.confirmingDeleteId === comment.id;
  }

  deleteComment(comment: Comment): void {
    if (this.inlineConfirm) {
      this.confirmingDeleteId = comment.id;
      return;
    }

    this._confirmService
      .openConfirmationModal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result: boolean) => result),
        switchMap(() => this._commentsService.deleteComment(comment.id)),
      )
      .subscribe(() => {
        this.loadComments();
        this.commentChanges.emit(true);
      });
  }

  confirmInlineDelete(comment: Comment): void {
    this.confirmingDeleteId = null;
    this._commentsService
      .deleteComment(comment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadComments();
        this.commentChanges.emit(true);
      });
  }

  cancelInlineDelete(): void {
    this.confirmingDeleteId = null;
  }

  cancelEditComment(comment: Comment): void {
    comment.editing = false;
  }

  updateComment(formDirective: FormGroupDirective): void {
    const comment = { ...this.editCommentForm.value };

    comment.body = this.mentionsService.transformMentionsForBackend(comment.body);

    this._commentsService
      .updateComment(comment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((comment) => {
        formDirective.resetForm();
        this.editCommentForm.reset();
        comment.editing = false;
        this.loadComments();
        this.commentChanges.emit(true);
      });
  }
}
